import { clsx } from "clsx";
import React, { FunctionComponent, ReactNode, RefObject } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

import { imageHost } from "../../config/config";
import { useLanguage } from "../../i18n/useLanguage";
import { VehicleInfo } from "../../queries/useGetVehicleInfo";
import { PackagePartItem } from "../../queries/useSearchPartPackagesByVehicle";
import { WheelPartItem } from "../../queries/useSearchWheelsByVehicle";
import { Button } from "../../ui/Button/Button";
import { Spinner } from "../../ui/Spinner";
import { replaceHostInUrl } from "../../util/replaceHostInUrl";
import { useAssetState } from "./useAssetState";
import {
  useVisualizerProductId,
  useVizualizerColourCode,
} from "./useVisualizer";

type VisualizerProps = {
  className?: string;
  compact?: boolean;
  controls: ReactNode;
  parts: (PackagePartItem | WheelPartItem)[] | null;
  portalRef?: RefObject<HTMLDivElement>;
  vehicleInfo: VehicleInfo | null;
};

export const Visualizer: FunctionComponent<VisualizerProps> = ({
  className,
  compact,
  controls,
  parts,
  portalRef,
  vehicleInfo,
}) => {
  const { t } = useTranslation();
  const [language] = useLanguage();
  const [selectedProductId] = useVisualizerProductId(parts);
  const [vehicleColourCode, setVehicleColourCode] = useVizualizerColourCode();

  const foregroundSrc = replaceHostInUrl(
    vehicleInfo?.vehicleVisualizerForegroundImageUrl,
    imageHost
  );

  const vehicleVisualizerShadowImageUrl = replaceHostInUrl(
    vehicleInfo?.vehicleVisualizerShadowImageUrl,
    imageHost
  );

  const [foregroundIsLoaded, setForegroundIsLoaded] =
    useAssetState(foregroundSrc);

  const colourCodeIndex =
    vehicleInfo?.vehicleColourCodes?.findIndex(
      ({ colourCodeId }) => colourCodeId === vehicleColourCode
    ) || 0;

  const colourCode =
    vehicleInfo?.vehicleColourCodes?.[Math.max(colourCodeIndex, 0)];

  const partSrc = parts?.find(
    ({ partDetails }) => partDetails.primaryPart.itemId === selectedProductId
  )?.partVisualizerAsset[0];

  return (
    <>
      <Helmet>
        {parts?.map(({ partDetails, partVisualizerAsset }) => (
          <link
            as="image"
            href={partVisualizerAsset[0]}
            key={partDetails.primaryPart.itemId}
            rel="preload"
          />
        ))}
      </Helmet>
      <section
        className={clsx(
          "border border-edge rounded-panel p-4 md:py-6 bg-white relative",
          className
        )}
      >
        <header>
          <h1 className="text-center">
            {vehicleInfo?.vehicleManufacturerName}{" "}
            {vehicleInfo?.vehicleSubmodelName?.[language]}
          </h1>
          <h4 className="text-center text-sm text-ink-2 font-thin">
            {t("searchResults.wheelVisualizerHint")}
          </h4>
        </header>
        <div className="flex items-center justify-center my-8">
          <div
            style={{
              backgroundColor:
                foregroundIsLoaded && colourCode?.colourHexCode
                  ? `#${colourCode?.colourHexCode}`
                  : "inherit",
            }}
            className="relative transition-colors duration-700"
          >
            {!foregroundIsLoaded && <Spinner className="mx-0" />}
            <img
              className={clsx(
                "block transition-opacity duration-700 aspect-[3/1] h-full",
                !foregroundIsLoaded && "hidden",
                compact
                  ? "max-width-[720px] max-h-[240px]"
                  : "max-width-[1200px] max-h-[400px]"
              )}
              style={{
                opacity:
                  (Number(colourCode?.visualizerShadowOpacity) || 50) / 100,
              }}
              alt=""
              src={vehicleVisualizerShadowImageUrl}
            />
            <img
              className={clsx(
                "absolute top-0 left-0 block aspect-[3/1]",
                compact ? "max-width-[720px]" : "max-width-[1200px]"
              )}
              alt=""
              onLoad={setForegroundIsLoaded}
              src={foregroundSrc}
            />
            <img alt="" className="absolute top-0 left-0 block" src={partSrc} />
          </div>
        </div>
        {controls && (
          <div className="md:absolute md:top-2 md:right-2 flex justify-center items-center">
            {controls}
          </div>
        )}
        <h4 className="text-center text-sm text-ink-2 font-thin mb-2 md:mb-0">
          {t("searchResults.wheelVisualizerCaution")}
        </h4>
        <nav
          className={clsx(
            "flex flex-wrap gap-1 justify-center user-select-none pointer-events-none",
            "md:flex-col md:gap-2 md:absolute md:w-[5.15rem] md:top-10 md:bottom-4",
            !foregroundIsLoaded && "invisible",
            compact
              ? "md:left-[1.4rem] md:items-start"
              : "md:right-[1.4rem] md:items-end"
          )}
        >
          {vehicleInfo?.vehicleColourCodes.map(
            ({
              colourCodeId,
              colourCodeName,
              colourCodePatchImage,
              colourHexCode,
            }) => (
              <Button
                className={clsx(
                  "w-6 h-6 md:w-8 md:h-8 bg-no-repeat bg-center bg-cover rounded-tw-full pointer-events-auto p-0 min-h-0",
                  "border border-edge transition shrink-0",
                  colourCodeId === colourCode?.colourCodeId
                    ? "!ring-1 !ring-offset-1 !md:ring-2 !md:ring-offset-2 !ring-ink"
                    : "ring-0"
                )}
                onClick={(): void => {
                  setVehicleColourCode(colourCodeId);
                }}
                style={{
                  backgroundColor: `#${colourHexCode}`,
                  backgroundImage: `url(${colourCodePatchImage})`,
                }}
                aria-label="vehicleColour"
                key={colourCodeId}
                title={colourCodeName[language]}
              />
            )
          )}
        </nav>
        {portalRef && (
          <div
            className="absolute right-4 bottom-4 w-1/5 min-w-[16rem]"
            ref={portalRef}
          />
        )}
      </section>
    </>
  );
};
